import { CircleHelp } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { useTimezone } from '@/hooks/useTimezone';
import { formatDateTime } from '@/lib/datetime';
import { guidesCollapsed } from '@/lib/guideDisplay';
import { scopeText } from '@/lib/scope';
import type { AsOf } from '@/services/types';

/**
 * The scope and freshness of everything on the screen.
 *
 * Three corpora with three different spans must never be read without both the
 * agency and the window, so this travels with the data rather than living in a
 * page title.
 *
 * It is also where the timezone selector does its work. `retrieved_at` is the
 * only true timestamp the API returns — every other date is a calendar date the
 * source recorded without a time — so formatting it here is what makes the
 * timezone control mean something rather than change a value nothing reads.
 *
 * The scope sentence is translated through `scope_key`, with `corpus_scope` as
 * the fallback. Same contract as a refusal's `reason_key`/`reason`: the English
 * is authored in the analytics layer and travels in the payload for exports and
 * clients without i18n, and the key names a translation for the ones that have
 * it. Several of these sentences carry the single fact that stops their screen
 * being misread — that PAF credit is not a fine, that the TFSEE is a levy — so
 * they are worth reading, which means they are worth translating.
 *
 * A `year_window` wins over `earliest`/`latest`. CADE dates its session archive
 * only by the folder each ata is filed under, so its window is years rather
 * than dates and carries the basis it was read on; `/cade/activity` sends both
 * forms, the year strings for its export's `corpus_span` and the window for the
 * screen, and the strip must not print the same span twice. The basis is a CODE
 * translated here, falling back to the code itself the way `SubstrateBadge`
 * does, so an unknown basis is visible rather than silently English.
 */
export function AsOfStrip({ asOf }: { asOf: AsOf }) {
  const { t } = useTranslation('common');
  const { timezone } = useTimezone();
  const scope = scopeText(asOf, t);
  const window = asOf.year_window ?? null;
  const basis = window
    ? `${t(`asOf.basis.${window.basis}`, { defaultValue: window.basis })}${
        window.checkable != null && window.agree != null
          ? `; ${t('asOf.basisAgreement', { agree: window.agree, checkable: window.checkable })}`
          : ''
      }`
    : '';

  return (
    // `data-as-of` so a test can wait for the STRIP rather than for the page
    // heading, which is static markup and renders before the query resolves --
    // read the page on the heading and you measure the loading state.
    <div
      data-as-of
      className="rounded-md border bg-muted/40 px-3 py-2 text-xs text-muted-foreground"
    >
      <span className="font-medium">{t('asOf.scope')}:</span> {scope}
      {window ? (
        <>
          {' · '}
          <span className="tabular-nums">
            {t('asOf.yearWindow', { first: window.first, last: window.last })}
          </span>{' '}
          {guidesCollapsed() ? (
            // How the years were read is a method note, not part of the answer:
            // behind a help icon on the pages that collapse their guides.
            <details className="inline align-middle" data-help>
              <summary
                aria-label={t('asOf.basisHelp')}
                title={t('asOf.basisHelp')}
                className="inline cursor-pointer list-none text-muted-foreground hover:text-foreground"
              >
                <CircleHelp className="inline h-3.5 w-3.5" aria-hidden />
              </summary>
              <span className="block pt-1">{basis}</span>
            </details>
          ) : (
            <>({basis})</>
          )}
        </>
      ) : asOf.earliest && asOf.latest ? (
        <>
          {' · '}
          {asOf.earliest} — {asOf.latest}
        </>
      ) : null}
      {asOf.retrieved_at ? (
        <>
          {' · '}
          <span className="font-medium">{t('asOf.retrieved')}:</span>{' '}
          <span className="tabular-nums">
            {formatDateTime(asOf.retrieved_at, timezone)} ({timezone})
          </span>
        </>
      ) : null}
    </div>
  );
}
